import { selectAllRoles } from '@user/store/roles/slice'
import { Product, RoleFormModel } from '@user/store/roles/typings'
import { FormikErrors, FormikHelpers } from 'formik'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  isAPIValidationError,
  trimString,
  updateLoadingState,
} from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { createRole, updateRole } from '@user/store/roles/api'
import { toast } from '@sales-center/components'
import {
  errorMessageCreate,
  errorMessageUpdate,
  successMessageCreate,
  successMessageUpdate,
} from '../constants'
import {
  selectUserPermissions,
  SettingsAdminPermOps,
  updateUserPermission,
} from '@user/store/authorization'

type Props = {
  role: RoleFormModel | undefined
  products: Product[]
  onClose: () => void
}

export function useFormWrapper({ role, products, onClose }: Props) {
  const roles = useSelector(selectAllRoles)
  const dispatch = useAppDispatch()
  const { permissions } = useSelector(selectUserPermissions)

  const onSubmit = useCallback(
    async (model: RoleFormModel, helpers: FormikHelpers<RoleFormModel>) => {
      dispatch(updateLoadingState('visible'))

      const isUpdate = model?.submitMode === 'update'
      const result = isUpdate
        ? await dispatch(updateRole(model))
        : await dispatch(createRole({ ...model, id: -1 })) // make sure id is -1

      if (
        createRole.fulfilled.match(result) ||
        updateRole.fulfilled.match(result)
      ) {
        if (model.name === 'Admin' && isUpdate) {
          const permissionKey = SettingsAdminPermOps
          const hasPermission = model.permissionOperations.includes(23)

          if (hasPermission && !permissions.includes(permissionKey)) {
            dispatch(updateUserPermission([...permissions, permissionKey]))
          } else if (!hasPermission) {
            dispatch(
              updateUserPermission(
                permissions.filter((perm) => perm !== permissionKey),
              ),
            )
          }
        }

        toast.success(
          model.submitMode === 'update'
            ? successMessageUpdate
            : successMessageCreate,
        )
        onClose()
        dispatch(updateLoadingState('hidden'))
      }

      if (
        createRole.rejected.match(result) ||
        updateRole.rejected.match(result)
      ) {
        const { payload } = result
        if (!!payload) {
          if (isAPIValidationError(payload)) {
            if (!!payload.errors) {
              const errors: FormikErrors<RoleFormModel> = {
                name: payload.errors['Name']?.pop(),
                permissionOperations:
                  payload.errors['PermissionOperations']?.pop(),
              }
              helpers.setErrors(errors)
            }
          }
        } else {
          toast.error(
            model.submitMode === 'update'
              ? errorMessageUpdate
              : errorMessageCreate,
          )
        }
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch, onClose, permissions],
  )
  const validate = (model: RoleFormModel) => {
    const errors: FormikErrors<RoleFormModel> = {}

    if (model.name.trim() == '') errors.name = 'Required'
    else if (
      roles.find(
        (role) =>
          role.id !== model.id &&
          role.name?.toLowerCase() === trimString(model.name?.toLowerCase()),
      )
    ) {
      errors.name = 'Role name already exists'
    }

    if (model.permissionOperations.length === 0)
      errors.permissionOperations = 'You must select at least one Permission'
    return errors
  }

  const initialValues = useMemo(() => {
    if (role) {
      return {
        ...role,
        submitMode: 'update',
      }
    }
    return {
      id: -1,
      name: '',
      permissionOperations: [],
      marketingProductId: products[0]?.id ?? '',
      submitMode: 'create',
    }
  }, [role, products])

  return {
    onSubmit,
    validate,
    initialValues,
  }
}
